import axios from 'axios';
import { toast } from 'sonner';
import { setAxiosInstance } from '@biasafe.api';
import { config } from './shared/config/env';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useUserStore } from '@/entities/user/store';
import { handleMfaRefusal } from '@/features/Auth/mfaReturn';
import {
  FIRM_SSO_REQUIRED,
  loginRedirectSearch,
} from '@/features/Auth/sessionNotices';
import {
  accessHeaders,
  clearAccess,
  currentAudience,
  isPortalHost,
} from '@/features/Auth/accessState';
import { usePortalSessionStore } from '@/features/ClientPortal/portalSession';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const apiClient = axios.create({
  baseURL: isPortalHost() ? '/v1' : config.api.baseUrl,
  timeout: 60000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((request) => {
  Object.entries(accessHeaders()).forEach(([name, value]) =>
    request.headers.set(name, value),
  );
  return request;
});

// --- 401 refresh interceptor ---
//
// Behavior:
//   1. On a 401 from any non-/auth/refresh request, attempt one silent
//      /auth/refresh round-trip and retry the original request.
//   2. If the refresh itself fails (4xx/5xx/timeout), or the retry STILL
//      returns 401, the session is dead — log out and hard-redirect to
//      /login. window.location is used instead of the router because
//      the failing request is often a background query that started
//      before navigation; only a full page reset cancels every in-flight
//      query cleanly.
//   3. While a refresh is in flight, concurrent 401s wait on a
//      pendingQueue and replay against the new session token afterwards.
//
// Loop guard:
//   `originalRequest._retry` is set on EVERY 401 the moment it enters
//   this handler — both for the initiating request and for any queued
//   followers. That way, if any request comes back with a 401 a second
//   time, we redirect instead of starting a fresh refresh dance. The
//   previous version only set _retry on the request that actually
//   triggered the refresh, which let queued followers loop a refresh
//   dance of their own when their retry also failed.

const REFRESH_RETRY_WINDOW_MS = 10_000;
const MAX_REFRESH_ATTEMPTS_IN_WINDOW = 3;
const refreshAttempts: Array<number> = [];

let isRefreshing = false;
let pendingQueue: Array<{
  resolve: () => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown) {
  for (const { resolve, reject } of pendingQueue) {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  }
  pendingQueue = [];
}

function hardRedirectToLogin(firmSsoRequired = false) {
  // A refresh started in the advisor shell may finish after a client opens
  // their invitation. That invitation is its own credential, not an expired
  // advisor session; never discard its form or the advisor's store from here.
  if (onHostedOnboarding()) return;
  const portal = currentAudience() === 'portal';
  clearAccess();
  if (portal) usePortalSessionStore.getState().logout();
  else useUserStore.getState().logout();
  if (
    typeof window !== 'undefined' &&
    !window.location.pathname.endsWith('/login')
  ) {
    // `?reason=` only when a sign-in completed moments ago (see
    // sessionNotices); a plain expiry keeps the bare /login. This function
    // never navigates when already on /login, so the message cannot start
    // another redirect.
    const search = firmSsoRequired
      ? { reason: FIRM_SSO_REQUIRED }
      : loginRedirectSearch();
    window.location.replace(
      portal
        ? '/portal/login'
        : search
          ? `/login?${new URLSearchParams(search).toString()}`
          : '/login',
    );
  }
}

function onHostedOnboarding(): boolean {
  return (
    typeof window !== 'undefined' &&
    /^\/onboard\/?$/.test(window.location.pathname)
  );
}

function refreshBudgetExceeded(): boolean {
  // Drop attempts older than the window so the counter only reflects
  // refresh attempts in the last few seconds.
  const cutoff = performance.now() - REFRESH_RETRY_WINDOW_MS;
  while (refreshAttempts.length && refreshAttempts[0] < cutoff) {
    refreshAttempts.shift();
  }
  return refreshAttempts.length >= MAX_REFRESH_ATTEMPTS_IN_WINDOW;
}

// --- 402 plan-limit prompt ---
//
// The backend gates (strategies, backtests, live models, team seats) return
// HTTP 402 with code "PL402PLR" when an org hits its tier ceiling. We surface
// a single upgrade toast (deduped within a short window so a burst of failed
// requests doesn't stack toasts) and still reject so the caller's catch runs.

const PLAN_LIMIT_CODE = 'PL402PLR';
let lastPlanLimitToastAt = 0;

function maybeShowPlanLimitPrompt(error: AxiosError): boolean {
  if (error.response?.status !== 402) return false;
  const detail = (
    error.response.data as { detail?: { code?: string } } | undefined
  )?.detail;
  if (detail?.code !== PLAN_LIMIT_CODE) return false;

  const now = Date.now();
  if (now - lastPlanLimitToastAt > 4000) {
    lastPlanLimitToastAt = now;
    toast.error("You've reached your plan's limit", {
      description: 'Upgrade to a paid plan to add more.',
      action: {
        label: 'Upgrade',
        onClick: () => window.location.assign('/settings'),
      },
    });
  }
  return true;
}

/**
 * Re-establish the session cookie using whichever provider is active.
 *
 * `/auth/refresh` belonged to the retired provider: it read the refresh-token
 * cookie and exchanged it server-side for a new access token. That endpoint
 * ALWAYS returns 401 by design — Firebase manages refresh client-side, so
 * `/auth/gcp/session` deliberately stores an empty refresh token. Calling it
 * anyway cost a guaranteed-failing round-trip on every 401 and surfaced
 * "Refresh token expired or invalid." to the user, including on the login
 * page where it means nothing.
 *
 * The gcp equivalent that actually works: ask Firebase for a fresh ID token
 * (force-refreshing if the cached one is stale) and re-exchange it for the
 * cookie. That is the same thing `subscribeIdTokenRefresh` does on rotation,
 * just driven by a 401 instead of a timer.
 */
export async function reestablishSession(): Promise<void> {
  {
    const { getCurrentIdToken } = await import('@/features/Auth/gcp/gcpAuth');
    const idToken = await getCurrentIdToken(true);
    if (!idToken) {
      // No Firebase user at all — nothing to re-establish, and retrying
      // would loop. Treated as a failed refresh by the caller.
      throw new Error('No Firebase session to re-establish');
    }
    const { exchangeAccessSession } =
      await import('@/features/Auth/accessSession');
    const result = await exchangeAccessSession(idToken);
    if (!result.selectedAccess)
      throw new Error('Choose an access before continuing');
    return;
  }

  // Tighter timeout than the global 60s — if the backend hasn't responded in 5
  // seconds the user is better served by being bounced to /login than by
  // every other query idling behind a stalled refresh round-trip.
  await apiClient.post('/auth/refresh', undefined, { timeout: 5000 });
}

/**
 * Whether a failed session refresh says nothing about the session itself.
 *
 * A timeout, a dropped connection or a 5xx means the API did not answer, not
 * that it refused us: the API restarting (every deploy) or being slow for a
 * moment. Logging out on those signed everybody out on each deploy, mid-form.
 * Only an answer that refuses the session, or no Firebase user at all, ends it.
 */
export function isTransientRefreshFailure(err: unknown): boolean {
  const e = err as AxiosError | undefined;
  if (!e || !e.isAxiosError) return false;
  const status = e.response?.status;
  if (status === undefined) return true;
  return status >= 500 || status === 408 || status === 429;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      CustomAxiosRequestConfig | undefined;
    if (!originalRequest) return Promise.reject(error);

    // Guest forms own their refusal/retry/expired-link screens. Neither a
    // public lookup nor a stale advisor-only request may send an invitation
    // holder to advisor sign-in or MFA enrolment. This only rejects the HTTP
    // error to its caller; it grants no access and changes no server auth.
    if (
      onHostedOnboarding() ||
      /(?:^|\/)public\/onboarding(?:\/|\?|$)/.test(originalRequest.url ?? '')
    ) {
      return Promise.reject(error);
    }

    if (maybeShowPlanLimitPrompt(error)) return Promise.reject(error);

    // A policy change cannot upgrade an existing password session. Clear the
    // local advisor state before showing login, otherwise its authenticated
    // route immediately sends the person back to the refused dashboard.
    if (
      error.response?.status === 403 &&
      (error.response.data as { detail?: { code?: string } } | undefined)
        ?.detail?.code === 'AU403SSO'
    ) {
      hardRedirectToLogin(true);
      return Promise.reject(error);
    }

    // A regulated action refused because the account has no second factor
    // (MFA_ENFORCEMENT_MODE=before_regulated_action). Not a dead session -- a
    // 403, so it never enters the refresh/logout path below -- but a prompt to
    // enrol, with the current page remembered so setup can bring them back.
    // Decodes a Blob body too: downloads use `responseType: 'blob'`, so their
    // error JSON arrives as a Blob and a plain property read would miss it.
    if (
      await handleMfaRefusal(error, {
        method: originalRequest.method,
        url: originalRequest.url,
      })
    ) {
      return Promise.reject(error);
    }

    const url = originalRequest.url ?? '';
    const isRefreshCall = url.includes('/auth/refresh');
    // On gcp, /auth/gcp/session is what re-establishes the session — so a 401
    // from it must never kick off another refresh, or the interceptor calls
    // itself. It is also NOT terminal: /auth/gcp/session answers 401 when the
    // email second factor is still outstanding, which the login flow handles.
    // Reject and let the caller decide.
    if (
      /\/auth\/(?:gcp\/session|accesses|mfa\/email\/challenge|logout)(?:\/|$)/.test(
        url,
      )
    )
      return Promise.reject(error);
    const is401 = error.response?.status === 401;

    if (!is401) return Promise.reject(error);

    // A 401 on the refresh endpoint itself is terminal.
    if (isRefreshCall) {
      hardRedirectToLogin();
      return Promise.reject(error);
    }

    // Loop guard: any 401 on a request that has already been retried
    // means refresh succeeded but the backend still won't authorize the
    // call. Stop trying.
    if (originalRequest._retry) {
      console.warn(
        '[auth] retry after refresh still returned 401, redirecting to /login',
        originalRequest.url,
      );
      hardRedirectToLogin();
      return Promise.reject(error);
    }

    // Burst guard: if we've kicked the refresh dance many times in a
    // short window without ever recovering, accept that this session
    // can't be saved and redirect. Without this, components that never
    // unmount on the redirect (long-running polls, retry-on-error
    // queries) can keep firing 401s after the redirect navigates away.
    if (refreshBudgetExceeded()) {
      console.warn(
        '[auth] refresh budget exceeded in window, redirecting to /login',
      );
      hardRedirectToLogin();
      return Promise.reject(error);
    }

    // Mark _retry on the originating request BEFORE queueing. This is
    // the critical fix: the previous version set _retry only on the
    // request that actually triggered the refresh, so queued followers
    // could loop again on their second 401.
    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    isRefreshing = true;
    refreshAttempts.push(performance.now());

    try {
      await reestablishSession();
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      if (isTransientRefreshFailure(refreshError)) {
        // The session may be fine; the API is not answering. Fail this one
        // request and let the next 401 try again, instead of logging out.
        return Promise.reject(error);
      }
      hardRedirectToLogin();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

setAxiosInstance(apiClient);

export default apiClient;
